import {
  AnsweringClient,
  AppliedCouponResponse,
  CartResponse,
} from "../clients/answeringClient";

export interface ICartItem {
  id: number;
  instrumentId: number;
  title: string;
  quantity: number;
  unitPriceCents: number;
  subtotalCents: number;
  discountedUnitPriceCents: number;
  discountedSubtotalCents: number;
  totalDiscountCents: number;
  appliedDiscountPct: number | null;
  priceSnapshotCents: number | null;
  priceChanged: boolean;
}

export interface ICartSnapshot {
  id: number;
  status: string;
  items: ICartItem[];
  itemsCount: number;
  subtotalCents: number;
  progressiveSubtotalCents: number;
  progressiveDiscountCents: number;
  couponDiscountCents: number;
  totalDiscountCents: number;
  totalCents: number;
  appliedCoupon: AppliedCouponResponse | null;
  updatedAt: string;
}

enum CartSingletonEvents {
  CART_UPDATED = "cartUpdated",
}

export class CartSingleton {
  private static _sharedInstance: CartSingleton;

  public static shared = (() => {
    if (!CartSingleton._sharedInstance) {
      CartSingleton._sharedInstance = new CartSingleton();
    }
    return CartSingleton._sharedInstance;
  })();

  private readonly notification: EventTarget =
    typeof BroadcastChannel !== "undefined"
      ? new BroadcastChannel("CartSingletonNotificationChannel")
      : new EventTarget();
  private readonly answeringClient = new AnsweringClient();
  private readonly listeners = new Map<() => void, EventListener>();
  private cart: ICartSnapshot | null = null;
  private couponCode: string | null = null;

  public addCartUpdatedListener(callback: () => void) {
    const handler: EventListener = () => callback();
    this.listeners.set(callback, handler);
    this.notification.addEventListener(CartSingletonEvents.CART_UPDATED, handler);
  }

  public removeCartUpdatedListener(callback: () => void) {
    const handler = this.listeners.get(callback);
    if (!handler) {
      return;
    }
    this.notification.removeEventListener(CartSingletonEvents.CART_UPDATED, handler);
    this.listeners.delete(callback);
  }

  public getCartSnapshot(): ICartSnapshot | null {
    return this.cart;
  }

  public getAllItems(): ICartItem[] {
    return this.cart?.items ?? [];
  }

  public getCartInstrument(instrumentId: number): ICartItem | null {
    if (!this.cart) {
      return null;
    }
    return (
      this.cart.items.find((item) => item.instrumentId === instrumentId) ?? null
    );
  }

  public async ensureLoaded(): Promise<ICartSnapshot> {
    if (this.cart) {
      return this.cart;
    }
    return this.refreshCart();
  }

  public async refreshCart(coupon?: string | null): Promise<ICartSnapshot> {
    const normalizedCoupon =
      coupon !== undefined
        ? coupon && coupon.trim()
          ? coupon.trim()
          : null
        : this.couponCode;

    const cart = await this.withAuth(async (token) => {
      const response = await this.answeringClient.getAdminCart(
        token,
        normalizedCoupon ?? undefined
      );
      return this.mapCartResponse(response.data);
    });

    this.couponCode = cart.appliedCoupon?.code ?? null;
    this.setCart(cart);
    return cart;
  }

  public async setCartInstrument(
    instrumentId: number,
    quantity: number
  ): Promise<void> {
    await this.ensureLoaded();
    const existingItem =
      this.cart?.items.find((item) => item.instrumentId === instrumentId) ?? null;

    if (!existingItem && quantity <= 0) {
      this.notification.dispatchEvent(new Event(CartSingletonEvents.CART_UPDATED));
      return;
    }

    if (existingItem && existingItem.quantity === quantity) {
      this.notification.dispatchEvent(new Event(CartSingletonEvents.CART_UPDATED));
      return;
    }

    if (quantity <= 0) {
      if (existingItem) {
        await this.withAuth((token) =>
          this.answeringClient.deleteCartItem(
            token,
            existingItem.id,
            this.couponCode ?? undefined
          )
        );
      }
    } else if (existingItem) {
      await this.withAuth((token) =>
        this.answeringClient.updateCartItem(token, existingItem.id, {
          quantity,
        }, this.couponCode ?? undefined)
      );
    } else {
      await this.withAuth((token) =>
        this.answeringClient.addCartItem(token, {
          instrumentId,
          quantity,
        }, this.couponCode ?? undefined)
      );
    }

    await this.refreshCart();
  }

  public async clear(): Promise<void> {
    await this.withAuth((token) => this.answeringClient.clearCart(token));
    await this.refreshCart();
  }

  public getAppliedCoupon(): AppliedCouponResponse | null {
    return this.cart?.appliedCoupon ?? null;
  }

  public getAppliedCouponCode(): string | null {
    return this.couponCode;
  }

  public async applyCoupon(code: string): Promise<void> {
    const trimmed = code?.trim();
    if (!trimmed) {
      throw new Error("Informe um código de cupom.");
    }

    const snapshot = await this.ensureLoaded();
    const baseAmount =
      snapshot.progressiveSubtotalCents ?? snapshot.subtotalCents ?? 0;

    try {
      const validated = await this.withAuth((token) =>
        this.answeringClient
          .validateCouponCode(token, {
            code: trimmed,
            orderAmountCents: baseAmount,
            cartId: snapshot.id,
          })
          .then((response) => response.data)
      );

      this.couponCode = validated.code;
      await this.refreshCart(validated.code);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.message ??
        "Não foi possível validar o cupom.";
      throw new Error(message);
    }
  }

  public async removeCoupon(): Promise<void> {
    if (!this.couponCode) {
      return;
    }
    this.couponCode = null;
    await this.refreshCart(null);
  }

  private setCart(cart: ICartSnapshot) {
    this.cart = {
      ...cart,
      items: [...cart.items],
      appliedCoupon: cart.appliedCoupon
        ? { ...cart.appliedCoupon }
        : null,
    };
    this.notification.dispatchEvent(new Event(CartSingletonEvents.CART_UPDATED));
  }

  private mapCartResponse(response: CartResponse): ICartSnapshot {
    return {
      id: response.id,
      status: response.status,
      items: (response.items ?? []).map((item) => ({
        id: item.id,
        instrumentId: item.instrumentId,
        title: item.title,
        quantity: item.quantity,
        unitPriceCents: item.unitPriceCents,
        subtotalCents: item.subtotalCents,
        discountedUnitPriceCents: item.discountedUnitPriceCents,
        discountedSubtotalCents: item.discountedSubtotalCents,
        totalDiscountCents: item.totalDiscountCents,
        appliedDiscountPct: item.appliedDiscountPct,
        priceSnapshotCents: item.priceSnapshotCents,
        priceChanged: item.priceChanged,
      })),
      itemsCount: response.itemsCount ?? 0,
      subtotalCents: response.subtotalCents ?? 0,
      progressiveSubtotalCents:
        response.progressiveSubtotalCents ?? response.subtotalCents ?? 0,
      progressiveDiscountCents: response.progressiveDiscountCents ?? 0,
      couponDiscountCents: response.couponDiscountCents ?? 0,
      totalDiscountCents: response.totalDiscountCents ?? 0,
      totalCents: response.totalCents ?? response.subtotalCents ?? 0,
      appliedCoupon: response.appliedCoupon
        ? { ...response.appliedCoupon }
        : null,
      updatedAt: response.updatedAt,
    };
  }

  private getAccessToken(): string | null {
    return localStorage.getItem("AccessToken");
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | null> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens = response.data as {
        accessToken: string;
        refreshToken: string;
      };
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error) {
      return null;
    }
  }

  private async withAuth<T>(
    operation: (token: string) => Promise<T>
  ): Promise<T> {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error("Missing access token");
    }

    try {
      return await operation(token);
    } catch (error) {
      const axiosError = error as {
        response?: {
          status?: number;
        };
      };

      if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
        const tokens = await this.refreshTokens();
        if (!tokens) {
          throw error;
        }
        return operation(tokens.accessToken);
      }

      throw error;
    }
  }
}
